import { useCallback, useMemo } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useActionContext } from './actionContext';
import { getShareActionConfig } from './shared/shareAction';
import type { ShareActionComponentProps, ShareTrackingDataOverride, ShareUrlInfo } from './types';

type Options<CH extends ShareChannel = ShareChannel> = {
	trackingEventName: string;
	config: ShareConfig;
	getShortUrlInfo: (channel: CH) => ShareUrlInfo;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onBeforeShare?: (channel: CH) => void;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

type Result =
	| {
			type: 'function';
			action: (extraTrackingData?: ShareTrackingDataOverride) => Promise<boolean>;
	  }
	| {
			type: 'component';
			Component: (props: ShareActionComponentProps) => React.JSX.Element;
	  };

export function useGetShareActionConfig<CH extends ShareChannel = ShareChannel>({
	getShortUrlInfo,
	getTrackingData,
	getContent,
	trackingEventName,
	config,
	onBeforeShare,
	onSuccess,
	onFailure,
}: Options<CH>): (channel: CH) => Result {
	const actionContext = useActionContext();
	return useCallback(
		(channel) => {
			const shareActionConfig = getShareActionConfig(channel);
			const shortUrlInfo = getShortUrlInfo(channel);
			const options = {
				url: shortUrlInfo.url,
				trackingEventName,
				trackingData: {
					...getTrackingData(channel),
					...shortUrlInfo.trackingProperties,
				},
				content: {
					title: getContent(channel, 'title', { url: shortUrlInfo.url }),
					body: getContent(channel, 'body', { url: shortUrlInfo.url }),
					image: getContent(channel, 'image', { url: shortUrlInfo.url }),
				},
				config,
			};
			if (shareActionConfig.type === 'function') {
				return {
					type: 'function' as const,
					action: async (extraTrackingData?: ShareTrackingDataOverride) => {
						onBeforeShare?.(channel);
						const success = await shareActionConfig.action(
							extraTrackingData
								? { ...options, trackingData: { ...options.trackingData, ...extraTrackingData } }
								: options,
							actionContext,
						);
						if (success) {
							void onSuccess?.(channel);
						} else {
							void onFailure?.(channel);
						}
						return success;
					},
				};
			}
			const { Component } = shareActionConfig;
			return {
				type: 'component' as const,
				Component: ({ onAfterAction, onBeforeAction, children, trackingData }: ShareActionComponentProps) => {
					// Runs before the caller's own onBeforeAction (which closes the slide-up for the
					// education-modal channels) so the cancellation lands while the sheet is still mounted.
					const onBeforeActionDecorated = useCallback(async () => {
						onBeforeShare?.(channel);
						await onBeforeAction?.();
					}, [onBeforeAction]);
					const onAfterActionDecorated = useCallback(
						(success: boolean) => {
							void onAfterAction?.(success);
							if (success) {
								void onSuccess?.(channel);
							} else {
								void onFailure?.(channel);
							}
						},
						[onAfterAction],
					);
					// Memoized (not inline) so component-type channels — which list `options` in their onClick
					// useMemo deps — keep hitting that memo instead of re-running on every render.
					const mergedOptions = useMemo(
						() => (trackingData ? { ...options, trackingData: { ...options.trackingData, ...trackingData } } : options),
						[options, trackingData],
					);
					return (
						<Component
							options={mergedOptions}
							onBeforeAction={onBeforeActionDecorated}
							onAfterAction={onAfterActionDecorated}
						>
							{children}
						</Component>
					);
				},
			};
		},
		[
			actionContext,
			getShortUrlInfo,
			getTrackingData,
			getContent,
			onBeforeShare,
			onSuccess,
			onFailure,
			trackingEventName,
			config,
		],
	);
}
